import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Eye, Edit3, Bold, Italic, List, Code, Heading1, Heading2, Link } from 'lucide-react';

type MarkdownBlockProps = {
  content: { markdown: string };
  onChange: (content: { markdown: string }) => void;
  previewMode?: boolean;
  style?: React.CSSProperties;
};

export const MarkdownBlock: React.FC<MarkdownBlockProps> = ({
  content,
  onChange,
  previewMode = false,
  style,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(content.markdown || '');

  const md = content.markdown || '';

  const commitEdit = useCallback(() => {
    onChange({ markdown: draft });
    setIsEditing(false);
  }, [draft, onChange]);

  const insertSyntax = useCallback(
    (before: string, after = '') => {
      const textarea = document.getElementById('md-editor-textarea') as HTMLTextAreaElement | null;
      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selected = draft.slice(start, end);
      const newValue = draft.slice(0, start) + before + selected + after + draft.slice(end);
      setDraft(newValue);
      // Restore cursor
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + before.length,
          start + before.length + selected.length,
        );
      }, 10);
    },
    [draft],
  );

  // Preview always if not editing
  const showPreview = !isEditing;

  if (previewMode || showPreview) {
    return (
      <div
        className="w-full h-full overflow-auto relative group"
        style={{ ...style, minHeight: '40px' }}
        onDoubleClick={() => !previewMode && setIsEditing(true)}
      >
        {/* Edit button on hover (only in editor mode) */}
        {!previewMode && (
          <button
            onClick={() => setIsEditing(true)}
            className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-white/80 backdrop-blur-sm rounded shadow-sm border border-gray-200"
            title="Edit markdown"
          >
            <Edit3 className="w-3.5 h-3.5 text-gray-600" />
          </button>
        )}
        {md ? (
          <div className="prose prose-sm max-w-none h-full px-1" style={style}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{md}</ReactMarkdown>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm italic">
            {previewMode ? '' : 'Double-click to edit markdown…'}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-white rounded overflow-hidden border border-blue-200">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 border-b border-gray-200 flex-shrink-0 flex-wrap">
        <span className="text-xs font-medium text-gray-500 mr-1">MD</span>
        <button
          onClick={() => insertSyntax('**', '**')}
          className="p-1 rounded hover:bg-gray-200 transition-colors"
          title="Bold"
        >
          <Bold className="w-3.5 h-3.5 text-gray-600" />
        </button>
        <button
          onClick={() => insertSyntax('_', '_')}
          className="p-1 rounded hover:bg-gray-200 transition-colors"
          title="Italic"
        >
          <Italic className="w-3.5 h-3.5 text-gray-600" />
        </button>
        <button
          onClick={() => insertSyntax('# ')}
          className="p-1 rounded hover:bg-gray-200 transition-colors"
          title="Heading 1"
        >
          <Heading1 className="w-3.5 h-3.5 text-gray-600" />
        </button>
        <button
          onClick={() => insertSyntax('## ')}
          className="p-1 rounded hover:bg-gray-200 transition-colors"
          title="Heading 2"
        >
          <Heading2 className="w-3.5 h-3.5 text-gray-600" />
        </button>
        <button
          onClick={() => insertSyntax('\n- ')}
          className="p-1 rounded hover:bg-gray-200 transition-colors"
          title="Bullet list"
        >
          <List className="w-3.5 h-3.5 text-gray-600" />
        </button>
        <button
          onClick={() => insertSyntax('`', '`')}
          className="p-1 rounded hover:bg-gray-200 transition-colors"
          title="Inline code"
        >
          <Code className="w-3.5 h-3.5 text-gray-600" />
        </button>
        <button
          onClick={() => insertSyntax('[', '](url)')}
          className="p-1 rounded hover:bg-gray-200 transition-colors"
          title="Link"
        >
          <Link className="w-3.5 h-3.5 text-gray-600" />
        </button>

        {/* Tab: Preview */}
        <div className="flex-1" />
        <button
          onClick={commitEdit}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          <Eye className="w-3 h-3" />
          Preview
        </button>
      </div>

      {/* Textarea */}
      <textarea
        id="md-editor-textarea"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          // Ctrl+Enter or Escape → commit
          if ((e.ctrlKey && e.key === 'Enter') || e.key === 'Escape') {
            e.preventDefault();
            commitEdit();
          }
        }}
        className="flex-1 resize-none font-mono text-sm p-2 outline-none border-none bg-gray-50 text-gray-800"
        placeholder={`Write markdown here...\n\n# Heading\n**bold** _italic_\n- list item\n\`inline code\``}
        spellCheck={false}
      />

      {/* Footer hint */}
      <div className="flex items-center justify-between px-2 py-1 bg-gray-50 border-t border-gray-200 text-xs text-gray-400 flex-shrink-0">
        <span>Ctrl+Enter or click Preview to apply</span>
        <span>{draft.length} chars</span>
      </div>
    </div>
  );
};
